import React, {useState} from 'react';

import {Button, Dialogue, Icon, Modal, Radio, Text, addToast} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';

import type {ReleaseStudentCounts} from './AssignmentsList.helpers';
import {useReleaseGradesMutation} from './queries/releaseGrades.queries.generated';

type ReleaseScope = 'class' | 'all';

const classListFormatter = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'});

interface ReleaseGradesModalProps {
  assignmentId: string;
  assignmentName: string;
  /**
   * The class the teacher is viewing; scopes a "This class only" release. Absent when entering from
   * All Classwork, where the release is every class the teacher teaches and there is no choice.
   */
  classroomId?: string;
  classroomName?: string;
  /** Who a release reaches in the class above. */
  studentCounts: ReleaseStudentCounts;
  /**
   * Whether this assignment reaches classes beyond the one being viewed. Only then does the
   * "Release for" choice appear — with a single class there is nothing to choose.
   */
  spansMultipleClasses?: boolean;
  /** Names of the classes an "All classes" release would reach, alphabetically. */
  classroomNames?: string[];
  /** The same counts across every class, for an "All classes" release. */
  allClassesStudentCounts?: ReleaseStudentCounts;
  /** True until the release scope above is known; confirming early could release too narrowly. */
  isScopeLoading?: boolean;
  /** The scope lookup failed, so "All classes" can't be offered. */
  hasScopeError?: boolean;
  /**
   * A class in scope shows results only after the due date, and that date is still ahead — so
   * releasing now leaves the student with nothing to see. Corrects the closing line, which
   * otherwise promises the grade appears immediately (#21337).
   */
  resultsHiddenUntilDueDate?: boolean;
  onClose: () => void;
}

export const ReleaseGradesModal = ({
  assignmentId,
  assignmentName,
  classroomId,
  classroomName,
  studentCounts,
  spansMultipleClasses,
  classroomNames,
  allClassesStudentCounts,
  isScopeLoading,
  hasScopeError,
  resultsHiddenUntilDueDate = false,
  onClose
}: ReleaseGradesModalProps) => {
  const [isReleasing, setIsReleasing] = useState(false);
  const [scope, setScope] = useState<ReleaseScope>('class');
  const [releaseGrades] = useReleaseGradesMutation();

  // Entered from All Classwork: there is no single class to narrow to, so the release is
  // all-classes by definition and the choice is suppressed.
  const isAllClassesEntry = !classroomId;
  const canChooseScope = !isAllClassesEntry && Boolean(spansMultipleClasses);
  const isAllClasses = isAllClassesEntry || (canChooseScope && scope === 'all');

  const counts = isAllClasses && allClassesStudentCounts ? allClassesStudentCounts : studentCounts;
  const withoutGradeCount = counts.awaitingGrade + counts.notSubmitted;

  const handleRelease = async () => {
    if (isReleasing) {
      return;
    }
    setIsReleasing(true);
    try {
      // Omitting classroomIds releases every class of the assignment the teacher teaches.
      const {data} = await releaseGrades({
        variables: {
          input:
            isAllClasses || !classroomId
              ? {assignmentId}
              : {assignmentId, classroomIds: [classroomId]}
        },
        // The mutation returns classroom assignments, so the normalized write covers
        // `gradesReleased` but nothing about the guesses the release sweep published. By name, so
        // this only costs a request where those reads are actually mounted (the submission view).
        refetchQueries: [
          'SubmissionGuessStatuses',
          'AssignmentGradingStatuses',
          'GuessesForGrading'
        ]
      });
      // The action silently skips classes the actor doesn't teach, so an empty list means
      // nothing was released — reporting success there would hide grades the teacher thinks
      // they published.
      if (!data?.releaseGrades.length) {
        addToast({
          color: 'negative',
          title: "Couldn't release grades",
          message: 'You can only release grades for classes you teach.'
        });
        setIsReleasing(false);
        return;
      }
      addToast({
        color: 'positive',
        title: 'Success!',
        message: `Released grades for ${assignmentName}.`
      });
      onClose();
    } catch (error) {
      addToast({
        color: 'negative',
        title: "Couldn't release grades",
        message: normalizeError(error).message
      });
      setIsReleasing(false);
    }
  };

  return (
    <Modal
      ariaLabel={`Release grades for ${assignmentName}`}
      handleClose={onClose}
      dismissable={!isReleasing}
    >
      {({modalContentStyle}) => (
        <Dialogue
          inModal
          size='m'
          alignTitle='left'
          title={`Release Grades for ${assignmentName}?`}
          handleClose={isReleasing ? undefined : onClose}
          className={modalContentStyle}
        >
          <Dialogue.Body>
            <div className='gap-x3 flex flex-col'>
              {canChooseScope ? (
                <fieldset className='gap-x1 p-x0 flex flex-col border-0'>
                  {/* A rendered legend is not a flex item, so the fieldset's gap skips it. */}
                  <Text as='legend' className='mb-x1 p-x0'>
                    Release for
                  </Text>
                  <ScopeOption
                    checked={scope === 'class'}
                    onChange={() => setScope('class')}
                    disabled={isReleasing}
                    label={`${classroomName} only`}
                    testId='release-scope-class'
                  />
                  <ScopeOption
                    checked={scope === 'all'}
                    onChange={() => setScope('all')}
                    disabled={isReleasing}
                    label='All classes'
                    description={
                      classroomNames?.length
                        ? `Release grades for students in ${classListFormatter.format(
                            classroomNames
                          )}.`
                        : 'Release grades for students in every class this is assigned to.'
                    }
                    testId='release-scope-all'
                  />
                </fieldset>
              ) : null}

              {hasScopeError ? (
                <Text as='p' size='s' color='warning' className='m-x0'>
                  {isAllClassesEntry
                    ? 'We couldn’t list your classes, but this will still release grades for every class you teach.'
                    : `We couldn’t check your other classes, so this will release for ${classroomName} only.`}
                </Text>
              ) : null}

              {withoutGradeCount > 0 ? (
                <div className='gap-x0-half flex flex-col'>
                  <Text
                    as='p'
                    bold
                    color='warning'
                    className='m-x0'
                    data-testid='release-students-without-grade'
                  >
                    {`${withoutGradeCount} of ${counts.total} ${
                      counts.total === 1 ? 'student' : 'students'
                    } will not see a grade`}
                  </Text>
                  <Text as='p' size='s' className='m-x0'>
                    {describeStudentsWithoutGrade(counts)}
                  </Text>
                </div>
              ) : null}

              <Text as='p' size='s' className='m-x0'>
                {resultsHiddenUntilDueDate
                  ? 'Releasing can’t be undone. This assignment shows results after the due date, so students will see their score then — not straight away.'
                  : 'Releasing can’t be undone. Students see their score and feedback as soon as you release.'}
              </Text>
            </div>
          </Dialogue.Body>
          <Dialogue.BtnGroup fillColumn>
            <Button
              variant='outlined'
              color='secondary'
              disabled={isReleasing}
              onClick={onClose}
              className='flex-1'
              data-testid='release-grades-cancel'
            >
              Cancel
            </Button>
            <Button
              color='primary'
              // Confirming before the scope resolves could release one class when the teacher
              // meant all of them.
              disabled={isReleasing || isScopeLoading}
              onClick={handleRelease}
              className='flex-1'
              data-testid='release-grades-confirm'
            >
              <span className='flex-1 text-center'>Release Grades</span>
              <Icon
                icon={isReleasing || isScopeLoading ? 'spinner' : 'paper-plane'}
                iconStyle='solid'
                spin={isReleasing || isScopeLoading}
              />
            </Button>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
};

/**
 * Why those students miss out, in the two ways a release can pass someone by. Both sentences are
 * student-level, so they add up to the headline above them however many FRQs the assignment has.
 */
const describeStudentsWithoutGrade = ({
  awaitingGrade,
  notSubmitted
}: ReleaseStudentCounts): string =>
  [
    awaitingGrade > 0
      ? `${awaitingGrade} student ${
          awaitingGrade === 1 ? 'submission' : 'submissions'
        } awaiting grade.`
      : null,
    notSubmitted > 0
      ? `${notSubmitted} ${notSubmitted === 1 ? 'student has' : 'students have'} not submitted.`
      : null
  ]
    .filter(Boolean)
    .join(' ');

interface ScopeOptionProps {
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
  label: string;
  description?: string;
  testId: string;
}

const ScopeOption = ({
  checked,
  onChange,
  disabled,
  label,
  description,
  testId
}: ScopeOptionProps) => (
  <label
    htmlFor={testId}
    className={`gap-x1-half rounded-x1 px-x2 py-x1-half flex cursor-pointer items-center ${
      checked
        ? 'border-border-brand background-brand-subtle border-2'
        : 'border-border-subtle border'
    }`}
  >
    <Radio
      id={testId}
      name='release-scope'
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className='shrink-0'
      data-testid={testId}
    />
    <span className='flex flex-col'>
      <Text bold>{label}</Text>
      {description ? (
        <Text size='s' color='secondary'>
          {description}
        </Text>
      ) : null}
    </span>
  </label>
);

export default ReleaseGradesModal;
